import { EnvironmentSelectorState } from '../../common/environment/reducer'
import { ResourceSelectorState } from '../../common/resource/reducer'
import { ResourceTargetSelectorState } from '../../common/resource/target/reducer'
import { ResourceType } from '@mulesoft/monitoring-center-components/lib/models/resource'
import {
    getResourceId,
    isARMApplicationDetail,
    isARMHybridDetail,
    isRTFApplication,
    Resource
} from '../../common/resource/model'
import { ARMApplicationDetail, ARMHybridDetail, RTFApplication } from '../../remote-api/models'
import { BasicTarget } from '../../common/resource/target/model';

export const rawDataOrigin = 'rawData' as 'rawData'

export enum RawDataResourceType {
    cloudhub = 'cloudhub',
    hybrid = 'hybrid',
    rtf = 'rtf',
    api = 'api'
}

export type LogFile = {
    envId: string
    appId: string
    fileName: string
    fileKey: string
    fileUrl: string
    workerId: string
    size: number
    date: Date
}

export type ArchiveAPIResources = {
    resources: ArchiveAPILogFile[]
}

export type ArchiveAPILogFile = {
    id: string
    time: string
    size: number
}

export type Date = {
  year: string
  month: string
  day: string
  hours: string
  minutes: string
}

export type RawDataWorker = {
  id: string
  name: string
}

export type RawDataState = {
    isLoading: boolean,
    selectedDate: string,
    logFiles: LogFile[]
    environmentSelector: EnvironmentSelectorState
    resourceSelector: ResourceSelectorState
    targetsSelector: ResourceTargetSelectorState
    allTargets: BasicTarget[]
}

export const getComponentsResourceType = (resourceType: RawDataResourceType) => {
    switch (resourceType) {
        case RawDataResourceType.cloudhub:
            return ResourceType.CLOUDHUB_APPLICATION
        case RawDataResourceType.hybrid:
            return ResourceType.HYBRID_APPLICATION
        case RawDataResourceType.rtf:
            return ResourceType.RTF_APPLICATION
        default:
            return ResourceType.API
    }
}

export const getRawDataResourceType = (resourceType: ResourceType) => {
    switch (resourceType) {
        case ResourceType.CLOUDHUB_APPLICATION:
            return RawDataResourceType.cloudhub
        case ResourceType.HYBRID_APPLICATION:
            return RawDataResourceType.hybrid
        case ResourceType.RTF_APPLICATION:
            return RawDataResourceType.rtf
        default:
            return RawDataResourceType.api
    }
}

export const getRawDataResourceTypeFromResource = (resource: Resource) => {
    if (isARMApplicationDetail(resource)) {
        return RawDataResourceType.cloudhub
    }

    if (isARMHybridDetail(resource)) {
        return RawDataResourceType.hybrid
    }

    if (isRTFApplication(resource)) {
        return RawDataResourceType.rtf
    }

    return undefined
}

export const getResourceLogsId = (resource: Resource, targets?: BasicTarget[]): string[] => {
    if (isARMHybridDetail(resource)) {
        return getHybridLogsId(resource, targets)
    }

    if (isRTFApplication(resource)) {
        return getRTFLogsId(resource, targets)
    }

    if (isARMApplicationDetail(resource)) {
        return getCloudHubLogsId(resource, targets)
    }

    return [getResourceId(resource)]
}

const getHybridLogsId = (app: ARMHybridDetail, targets?: BasicTarget[]) => {
    if (app.target.servers) {
        const servers = [app.target, ...app.target.servers]
        if (targets) {
            return servers.filter(s => targets.findIndex(t => t.id === s.id.toString()) !== -1).map(s => `${app.name}_${s.id}`)
        } else {
            return servers.map(s => `${app.name}_${s.id}`)
        }
    } else {
        return [`${app.name}_${app.target.id}`]
    }
}

const getRTFLogsId = (app: RTFApplication, targets?: BasicTarget[]) => {
    if (targets) {
        return app.replicas.filter(r => targets.findIndex(t => r.id && t.id === r.id.toString()) !== -1).map(r => `${app.name}_${r.id}`)
    } else {
        return app.replicas.map(r => `${app.name}_${r.id}`)
    }
}

const getCloudHubLogsId = (app: ARMApplicationDetail, targets?: BasicTarget[]) => {
    const workersLength = app.workers.amount
    const ids = []

    for (let i = 0; i < workersLength; i++) {
        if (targets) {
            if (targets.find(t => t.id === i.toString())) {
                ids.push(`${app.fullDomain}_${i}`)
            }
        } else {
            ids.push(`${app.fullDomain}_${i}`)
        }
    }

    return ids
}

export const getFileUrl = (orgId: string, envId: string, resourceLogId: string, date: Date, fileId: string, resource: Resource) => {
    const resourceType = getRawDataResourceTypeFromResource(resource)

    if (resourceType === RawDataResourceType.hybrid) {
        return `organizations/${orgId}/environments/${envId}/servers/${resourceLogId}/logs/${date.year}/${date.month}/${date.day}/${fileId}`
    } else {
        return `organizations/${orgId}/environments/${envId}/applications/${resourceLogId}/logs/${date.year}/${date.month}/${date.day}/${fileId}`
    }
}

export const getStringCurrentDate = () => {
    const today = new Date()
    const year = today.getFullYear()
    const month = today.getMonth() + 1
    const day = today.getDate()
    return `${month < 10 ? '0' + month : month}/${day < 10 ? '0' + day : day}/${year}`
}
